import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import * as React from 'react';
import styled from 'styled-components';
import { spacing1x } from '../../styles/spacing.js';
import { useThemeContext } from '../../styles/themes/themes.js';
import { Circle } from '../Circle/Circle.js';
import { IconStatusCheckFilled } from '../Icons/IconStatusCheckFilled.js';
import { IconStatusInformationFilled } from '../Icons/IconStatusInformationFilled.js';
import { IconStatusUrgentFilled } from '../Icons/IconStatusUrgentFilled.js';
import { LinkButton } from '../LinkButton/LinkButton.js';
import { Truncate } from '../Truncate/Truncate.js';
import { jsx, jsxs } from 'react/jsx-runtime';

var _excluded = ["displayCount", "onNodeClick", "orientation", "sections"];
var ProgressStepperContainer = styled.div.withConfig({
  displayName: "ProgressStepper__ProgressStepperContainer",
  componentId: "sc-15v2i21-0"
})(["&&{", ";}"], function (props) {
  return props.isHorizontal ? "overflow-x: auto;" : "overflow-y: auto; height: 100%;";
});
var StyledCircle = styled(Circle).withConfig({
  displayName: "ProgressStepper__StyledCircle",
  componentId: "sc-15v2i21-1"
})(["&&{color:", ";background-color:", ";border:1px solid ", ";", ";", ";", ";}"], function (props) {
  return props.theme.neutralColors.charcoal;
}, function (props) {
  return props.theme.neutralColors.white;
}, function (props) {
  return props.theme.neutralColors.charcoal;
}, function (props) {
  return props.status === "active" && "border-color: ".concat(props.theme.alertColors.success, ";");
}, function (props) {
  return props.status === "disabled" && "border-color: ".concat(props.theme.neutralColors.rhino, ";\n    ");
}, function (props) {
  return props.size === "xs" && "font-size: 10px";
});
var LabelContainer = styled.div.withConfig({
  displayName: "ProgressStepper__LabelContainer",
  componentId: "sc-15v2i21-2"
})(["&&{", ";}[dir='rtl'] &&{", ";}"], function (props) {
  return props.isHorizontal ? "transform: translateX(-50%);\n               margin-top: ".concat(spacing1x, ";") : "margin-inline-start: ".concat(spacing1x, ";\n               height: min-content;\n               transform: translateY(-50%);");
}, function (props) {
  return props.isHorizontal ? "transform: translateX(50%)};" : "";
});
var NodeWrapper = styled.div.withConfig({
  displayName: "ProgressStepper__NodeWrapper",
  componentId: "sc-15v2i21-3"
})(function (props) {
  return props.isHorizontal ? {
    borderTop: "2px solid ".concat(props.theme.neutralColors.smoke),
    minWidth: 85,
    width: "100%"
  } : {
    borderInlineStart: "2px solid ".concat(props.theme.neutralColors.smoke),
    minHeight: 32,
    height: "100%",
    display: "flex"
  };
});
var NodeStatus = styled.div.withConfig({
  displayName: "ProgressStepper__NodeStatus",
  componentId: "sc-15v2i21-4"
})(["&&{height:32px;width:32px;justify-content:center;display:flex;align-items:center;", ";}"], function (props) {
  return props.isHorizontal ? "margin-top: -17px; margin-inline-start: -16px;" : "margin-inline-start: -17px; margin-top: -16px";
});
var ProgressStepperBar = styled.div.withConfig({
  displayName: "ProgressStepper__ProgressStepperBar",
  componentId: "sc-15v2i21-5"
})(["&&{display:flex;justify-content:space-between;margin:20px;", ";}"], function (props) {
  return props.isHorizontal ? "".concat(NodeWrapper, ":first-child {\n                    margin-inline-start: 30px;\n               }\n               ").concat(NodeWrapper, ":last-child {\n                   min-width: 0;\n                   width: 0;\n                   margin-inline-end: 30px;\n               }") : "flex-direction: column;\n               height: 100%;\n               ".concat(NodeWrapper, ":last-child {\n                   min-height: 0;\n                   height: 0;\n               }");
});
var StyledLinkButton = styled(LinkButton).withConfig({
  displayName: "ProgressStepper__StyledLinkButton",
  componentId: "sc-15v2i21-6"
})(["&&{font-weight:normal;}&&:hover&&:hover,&&:focus&&:focus,&&:active&&:active{color:", ";}"], function (props) {
  return !props.disabled && props.theme.neutralColors.charcoal;
});
var Node = function Node(props) {
  var displayCount = props.displayCount,
    index = props.index,
    isHorizontal = props.isHorizontal,
    isSectionStep = props.isSectionStep,
    nodeProps = props.nodeProps,
    onNodeClick = props.onNodeClick;
  var children = nodeProps.children,
    _nodeProps$status = nodeProps.status,
    status = _nodeProps$status === void 0 ? "default" : _nodeProps$status;
  var theme = useThemeContext();
  var maxIconSize = isHorizontal ? 24 : 16;
  var maxCircleSize = isHorizontal ? "sm" : "xs";
  var iconSize = isSectionStep ? 10 : maxIconSize;
  var circleSize = isSectionStep ? "xxs" : maxCircleSize;
  var circleContent = displayCount ? index + 1 : null;
  var nodeStatus;
  switch (status) {
    case "active":
      nodeStatus = /*#__PURE__*/jsx(StyledCircle, {
        size: circleSize,
        status: "active",
        children: circleContent
      });
      break;
    case "completed":
      nodeStatus = /*#__PURE__*/jsx(IconStatusCheckFilled, {
        color: theme.alertColors.success,
        height: iconSize,
        width: iconSize
      });
      break;
    case "warning":
      nodeStatus = /*#__PURE__*/jsx(IconStatusInformationFilled, {
        color: theme.alertColors.warning,
        height: iconSize,
        width: iconSize
      });
      break;
    case "alert":
      nodeStatus = /*#__PURE__*/jsx(IconStatusUrgentFilled, {
        color: theme.alertColors.error,
        height: iconSize,
        width: iconSize
      });
      break;
    case "disabled":
      nodeStatus = /*#__PURE__*/jsx(StyledCircle, {
        size: circleSize,
        status: "disabled",
        children: circleContent
      });
      break;
    default:
      nodeStatus = /*#__PURE__*/jsx(StyledCircle, {
        size: circleSize,
        children: circleContent
      });
  }
  var labelStyle = _objectSpread(_objectSpread(_objectSpread({}, status === "disabled" ? {
    color: theme.neutralColors.rhino
  } : {}), status === "active" ? {
    fontWeight: 600
  } : {}), status === "alert" ? {
    color: theme.alertColors.error
  } : {});
  var labelJSX = onNodeClick ? /*#__PURE__*/jsx(StyledLinkButton, {
    label: children,
    onClick: function onClick() {
      return onNodeClick(nodeProps);
    },
    variant: "secondary",
    style: labelStyle,
    disabled: status === "disabled"
  }) : /*#__PURE__*/jsx("span", {
    tabIndex: 0,
    style: labelStyle,
    children: children
  });
  return /*#__PURE__*/jsxs(NodeWrapper, {
    isHorizontal: isHorizontal,
    children: [/*#__PURE__*/jsx(NodeStatus, _objectSpread(_objectSpread({
      isHorizontal: isHorizontal,
      role: "listitem"
    }, onNodeClick ? _objectSpread(_objectSpread({}, status === "disabled" ? {} : {
      onClick: function onClick() {
        return onNodeClick(nodeProps);
      }
    }), {}, {
      style: {
        cursor: "pointer"
      }
    }) : {}), {}, {
      children: nodeStatus
    })), /*#__PURE__*/jsx(Truncate, {
      tooltipContent: children,
      maxCharLength: isHorizontal ? 10 : 25,
      children: /*#__PURE__*/jsx(LabelContainer, {
        isHorizontal: isHorizontal,
        style: {
          flex: 1
        },
        children: labelJSX
      })
    })]
  });
};
Node.displayName = "ProgressStepperNode";
var ProgressStepper = /*#__PURE__*/React.forwardRef(function ProgressStepper2(props, ref) {
  var _props$displayCount = props.displayCount,
    displayCount = _props$displayCount === void 0 ? true : _props$displayCount,
    onNodeClick = props.onNodeClick,
    _props$orientation = props.orientation,
    orientation = _props$orientation === void 0 ? "horizontal" : _props$orientation,
    sections = props.sections,
    rest = _objectWithoutProperties(props, _excluded);
  var nodes = [];
  var isHorizontal = orientation === "horizontal";
  var nodeCounter = 0;
  sections.forEach(function (section, index) {
    var _section$steps;
    nodes.push( /*#__PURE__*/jsx(Node, {
      nodeProps: section,
      isHorizontal: isHorizontal,
      index: index,
      displayCount: displayCount,
      onNodeClick: onNodeClick
    }, nodeCounter));
    nodeCounter++;
    if ((_section$steps = section.steps) !== null && _section$steps !== void 0 && _section$steps.length) {
      section.steps.forEach(function (sectionStep, idx) {
        nodes.push( /*#__PURE__*/jsx(Node, {
          nodeProps: sectionStep,
          isHorizontal: isHorizontal,
          index: idx,
          isSectionStep: true,
          onNodeClick: onNodeClick
        }, nodeCounter));
        nodeCounter++;
      });
    }
  });
  return /*#__PURE__*/jsx(ProgressStepperContainer, _objectSpread(_objectSpread({
    isHorizontal: isHorizontal,
    ref: ref,
    role: "list",
    tabIndex: 0
  }, rest), {}, {
    children: /*#__PURE__*/jsx(ProgressStepperBar, {
      isHorizontal: isHorizontal,
      children: nodes
    })
  }));
});
ProgressStepper.displayName = "ProgressStepper";

export { ProgressStepper };
